/**
 * Import Column Mapper Utility
 * 
 * Utility functions for mapping CSV columns to transaction fields
 * Includes bank format detection and suggested column mappings
 */

import type { ColumnMapping, ParsedTransaction } from '../types/import.types';
import { parseAustralianDateToISO } from './date-parser';

/** Substrings that suggest the value is address/location data, not a date */
const ADDRESS_LIKE_PATTERNS = [
  'AUSTRALIA', 'UNITED STATES', 'NSW', 'VIC', 'QLD', 'SA ', 'WA ', 'TAS', 'ACT', 'NT ',
  'FLOOR', 'STE ', 'STREET', 'AVENUE', 'ROAD', 'GREENWICH', 'SAN FRANCISCO', 'CA,', ',AUSTRALIA', ',UNITED STATES'
];

function looksLikeAddressOrLocation(value: string): boolean {
  const upper = value.toUpperCase();
  return ADDRESS_LIKE_PATTERNS.some(p => upper.includes(p)) ||
    /,\s*\d{4,5}\s*,/.test(value) || // e.g. ",2067," or ",94114,"
    /'AT\d+'\s*$/.test(value.trim()); // Amex reference at end
}

/**
 * Bank format types
 */
export type BankFormat = 'CBA' | 'ANZ' | 'Westpac' | 'NAB' | 'Unknown';

/**
 * Detect bank format from CSV columns
 * @param columns - CSV column names
 * @returns Detected bank format
 */
export function detectBankFormat(columns: string[]): BankFormat {
  const columnNames = columns.map(col => col.toLowerCase().trim());

  // CBA format indicators
  if (columnNames.includes('transaction date') && 
      columnNames.includes('narration') && 
      (columnNames.includes('debit') || columnNames.includes('credit'))) {
    return 'CBA';
  }

  // ANZ format indicators
  if (columnNames.includes('date') && 
      columnNames.includes('description') && 
      (columnNames.includes('debit') || columnNames.includes('credit'))) {
    return 'ANZ';
  }

  // Westpac format indicators
  if (columnNames.includes('transaction date') && 
      columnNames.includes('transaction description') && 
      (columnNames.includes('debit') || columnNames.includes('credit'))) {
    return 'Westpac';
  }

  // NAB format indicators
  if (columnNames.includes('date') && 
      columnNames.includes('transaction description') && 
      (columnNames.includes('debit') || columnNames.includes('credit'))) {
    return 'NAB';
  }

  return 'Unknown';
}

/**
 * Get suggested column mappings for bank format
 * @param bankFormat - Bank format
 * @param columns - CSV column names
 * @returns Suggested column mappings
 */
export function getSuggestedMappings(bankFormat: BankFormat, columns: string[]): ColumnMapping[] {
  const mappings: ColumnMapping[] = [];
  const columnMap = new Map(columns.map((col, index) => [col.toLowerCase().trim(), index]));

  switch (bankFormat) {
    case 'CBA':
    case 'ANZ':
    case 'Westpac':
    case 'NAB':
      // Common Australian bank format
      if (columnMap.has('transaction date')) {
        mappings.push({
          csvColumn: columns[columnMap.get('transaction date')!],
          mappedField: 'date',
          isRequired: true
        });
      } else if (columnMap.has('date')) {
        mappings.push({
          csvColumn: columns[columnMap.get('date')!],
          mappedField: 'date',
          isRequired: true
        });
      }

      if (columnMap.has('debit')) {
        mappings.push({
          csvColumn: columns[columnMap.get('debit')!],
          mappedField: 'debit',
          isRequired: false
        });
      }

      if (columnMap.has('credit')) {
        mappings.push({
          csvColumn: columns[columnMap.get('credit')!],
          mappedField: 'credit',
          isRequired: false
        });
      }

      if (columnMap.has('narration')) {
        mappings.push({
          csvColumn: columns[columnMap.get('narration')!],
          mappedField: 'description',
          isRequired: true
        });
      } else if (columnMap.has('description')) {
        mappings.push({
          csvColumn: columns[columnMap.get('description')!],
          mappedField: 'description',
          isRequired: true
        });
      } else if (columnMap.has('transaction description')) {
        mappings.push({
          csvColumn: columns[columnMap.get('transaction description')!],
          mappedField: 'description',
          isRequired: true
        });
      }

      if (columnMap.has('balance')) {
        mappings.push({
          csvColumn: columns[columnMap.get('balance')!],
          mappedField: 'balance_after',
          isRequired: false
        });
      }

      if (columnMap.has('cheque number')) {
        mappings.push({
          csvColumn: columns[columnMap.get('cheque number')!],
          mappedField: 'cheque_number',
          isRequired: false
        });
      }

      if (columnMap.has('transaction type')) {
        mappings.push({
          csvColumn: columns[columnMap.get('transaction type')!],
          mappedField: 'type',
          isRequired: false
        });
      }
      break;

    default:
      // Check for single Amount column format (e.g., AMEX)
      if (columnMap.has('amount') && !columnMap.has('debit') && !columnMap.has('credit')) {
        // Single Amount column detected
        if (columnMap.has('date') || columnMap.has('transaction date')) {
          const dateCol = columnMap.has('transaction date') ? 'transaction date' : 'date';
          mappings.push({
            csvColumn: columns[columnMap.get(dateCol)!],
            mappedField: 'date',
            isRequired: true
          });
        }

        mappings.push({
          csvColumn: columns[columnMap.get('amount')!],
          mappedField: 'amount',
          isRequired: false
        });

        if (columnMap.has('description')) {
          mappings.push({
            csvColumn: columns[columnMap.get('description')!],
            mappedField: 'description',
            isRequired: true
          });
        }
      }
      break;
  }

  return mappings;
}

/**
 * Parse transaction row from CSV data
 * @param row - CSV row data (object with column names as keys)
 * @param mappings - Column mappings
 * @param amountInvertSign - Optional flag for amount sign interpretation (defaults to true)
 *   - true (Yes): Positive = debit, Negative = credit (liability behavior)
 *   - false (No): Positive = credit, Negative = debit (asset behavior)
 * @returns Parsed transaction or null if invalid
 * @throws Error with descriptive message if parsing fails
 */
export function parseTransactionRow(
  row: Record<string, string>,
  mappings: ColumnMapping[],
  amountInvertSign: boolean = true
): ParsedTransaction | null {
  const transaction: Partial<ParsedTransaction> = {};

  // Get mapped values
  const dateMapping = mappings.find(m => m.mappedField === 'date');
  const debitMapping = mappings.find(m => m.mappedField === 'debit');
  const creditMapping = mappings.find(m => m.mappedField === 'credit');
  const amountMapping = mappings.find(m => m.mappedField === 'amount');
  const descriptionMapping = mappings.find(m => m.mappedField === 'description');
  const merchantNameMapping = mappings.find(m => m.mappedField === 'merchant_name');
  const referenceNumberMapping = mappings.find(m => m.mappedField === 'reference_number');
  const externalIdMapping = mappings.find(m => m.mappedField === 'external_id');
  const balanceAfterMapping = mappings.find(m => m.mappedField === 'balance_after');
  const chequeNumberMapping = mappings.find(m => m.mappedField === 'cheque_number');
  const typeMapping = mappings.find(m => m.mappedField === 'type');

  // Parse date
  if (dateMapping) {
    const dateValue = row[dateMapping.csvColumn];
    if (dateValue) {
      const isoDate = parseAustralianDateToISO(dateValue);
      if (!isoDate) {
        // If value looks like address/location data, suggest checking column mapping
        if (looksLikeAddressOrLocation(dateValue)) {
          throw new Error(
            'The column mapped as Date contains address or location data. Please go back to Step 2 (Column Mapping) and select the correct Date column (e.g. "Date" or "Transaction Date").'
          );
        }
        throw new Error(`Invalid date format: "${dateValue}"`);
      }
      transaction.date = isoDate;
    } else if (dateMapping.isRequired) {
      throw new Error('Required field missing: Date');
    }
  }

  // Parse debit/credit/amount columns
  // Remove currency symbols, commas, and whitespace before parsing
  const cleanNumber = (value: string): number => {
    if (!value || value.trim() === '') return NaN;
    // Remove currency symbols, commas, and whitespace
    const cleaned = value.trim().replace(/[$,\s]/g, '');
    return parseFloat(cleaned);
  };

  // Check if amount column is mapped (single column format)
  const amountRaw = amountMapping ? (row[amountMapping.csvColumn] || '').trim() : '';
  
  if (amountMapping) {
    // Single Amount column format
    // Validate: cannot have both amount and debit/credit mappings
    if (debitMapping || creditMapping) {
      throw new Error('Cannot map both Amount column and Debit/Credit columns. Please use either Amount OR Debit/Credit columns.');
    }

    if (amountRaw === '') {
      throw new Error('No amount found: Amount column is empty');
    }

    const amountValue = cleanNumber(amountRaw);
    
    if (isNaN(amountValue)) {
      throw new Error(`Invalid amount value: "${amountRaw}"`);
    }

    // Handle zero amounts
    if (amountValue === 0) {
      return null; // Skip zero-amount transactions
    }

    // Determine transaction type based on sign and amountInvertSign setting
    // amountInvertSign === true (Yes - default): Positive = debit, Negative = credit (liability behavior)
    // amountInvertSign === false (No): Positive = credit, Negative = debit (asset behavior)
    if (amountInvertSign) {
      // Liability behavior: Positive = debit, Negative = credit
      if (amountValue > 0) {
        transaction.type = 'debit';
      } else {
        transaction.type = 'credit';
      }
    } else {
      // Asset behavior: Positive = credit, Negative = debit
      if (amountValue > 0) {
        transaction.type = 'credit';
      } else {
        transaction.type = 'debit';
      }
    }

    // Store absolute value (amount is always positive in database)
    transaction.amount = Math.abs(amountValue);
  } else {
    // Two-column format (Debit/Credit)
    const debitRaw = debitMapping ? (row[debitMapping.csvColumn] || '').trim() : '';
    const creditRaw = creditMapping ? (row[creditMapping.csvColumn] || '').trim() : '';
    
    const debitValue = debitRaw ? Math.abs(cleanNumber(debitRaw)) : 0;
    const creditValue = creditRaw ? Math.abs(cleanNumber(creditRaw)) : 0;
    
    const hasDebit = debitRaw !== '' && !isNaN(debitValue) && debitValue > 0;
    const hasCredit = creditRaw !== '' && !isNaN(creditValue) && creditValue > 0;
    
    // Check if either value is explicitly zero (even if the other is empty)
    const isZeroAmount = (debitRaw !== '' && !isNaN(debitValue) && debitValue === 0) ||
                         (creditRaw !== '' && !isNaN(creditValue) && creditValue === 0) ||
                         (debitRaw !== '' && creditRaw !== '' && debitValue === 0 && creditValue === 0);

    // Validate: both debit and credit cannot have values
    if (hasDebit && hasCredit) {
      throw new Error('Both debit and credit columns cannot have values for the same transaction');
    }

    // Handle zero-amount transactions (e.g., waived fees) - return null instead of error
    if (isZeroAmount || (!hasDebit && !hasCredit)) {
      // Check if both are truly empty (not provided) vs explicitly zero
      const bothEmpty = (debitRaw === '' || debitRaw === null || debitRaw === undefined) &&
                        (creditRaw === '' || creditRaw === null || creditRaw === undefined);
      
      if (bothEmpty) {
        // Both columns are empty - this is still an error (missing required data)
        throw new Error('No amount found: both Debit and Credit columns are empty');
      }
      
      // One or both are explicitly zero - return null (will be skipped as zero-amount transaction)
      return null;
    }

    // Set amount and type (use absolute value for amount)
    if (hasDebit) {
      transaction.amount = debitValue;
      transaction.type = 'debit';
    } else if (hasCredit) {
      transaction.amount = creditValue;
      transaction.type = 'credit';
    }
  }

  // Parse description
  if (descriptionMapping) {
    const descValue = row[descriptionMapping.csvColumn];
    if (descValue) {
      transaction.description = descValue.trim();
    } else if (descriptionMapping.isRequired) {
      throw new Error('Required field missing: Description');
    }
  }

  // Parse optional fields
  if (merchantNameMapping) {
    const merchantValue = row[merchantNameMapping.csvColumn];
    if (merchantValue) {
      transaction.merchantName = merchantValue.trim();
    }
  }

  if (referenceNumberMapping) {
    const refValue = row[referenceNumberMapping.csvColumn];
    if (refValue) {
      transaction.referenceNumber = refValue.trim();
    }
  }

  if (externalIdMapping) {
    const extValue = row[externalIdMapping.csvColumn];
    if (extValue) {
      transaction.externalId = extValue.trim();
    }
  }

  if (balanceAfterMapping) {
    const balanceValue = row[balanceAfterMapping.csvColumn];
    if (balanceValue) {
      const balanceNum = parseFloat(balanceValue);
      if (!isNaN(balanceNum)) {
        transaction.balanceAfter = balanceNum;
      }
    }
  }

  if (chequeNumberMapping) {
    const chequeValue = row[chequeNumberMapping.csvColumn];
    if (chequeValue) {
      transaction.chequeNumber = chequeValue.trim();
    }
  }

  // Parse transaction type from mapping if provided
  if (typeMapping) {
    const typeValue = row[typeMapping.csvColumn];
    if (typeValue) {
      // Map bank transaction type codes to our types
      const typeCode = typeValue.trim().toUpperCase();
      if (typeCode === 'TFD') {
        // Transfer Debit - convert to regular debit for CSV imports
        // (CSV imports don't support transfers as they require source/destination accounts)
        transaction.type = 'debit';
      } else if (typeCode === 'TFC') {
        // Transfer Credit - convert to regular credit for CSV imports
        // (CSV imports don't support transfers as they require source/destination accounts)
        transaction.type = 'credit';
      } else if (typeCode === 'CRI' || typeCode === 'DEP') {
        transaction.type = 'credit';
      } else if (typeCode === 'WDC' || typeCode === 'WDL') {
        transaction.type = 'debit';
      }
    }
  }

  // Validate required fields
  if (!transaction.date) {
    throw new Error('Missing required field: Date');
  }
  if (!transaction.amount || transaction.amount === 0) {
    throw new Error('Missing required field: Amount');
  }
  if (!transaction.description) {
    throw new Error('Missing required field: Description');
  }
  if (!transaction.type) {
    throw new Error('Missing required field: Transaction Type');
  }

  return transaction as ParsedTransaction;
}

